"use client";

import { usePathname } from "next/navigation";
import {
	HmcButton,
	HmcIcon,
	HmcNavLink,
} from "@adi-ctx/harmonic-core-components-react";
import HeaderUser from "./components/HeaderUser/HeaderUser";
import { useAuth } from "@/components";
import t from "@/utils/translate.utils";
import {
	Logo,
	type TDropdownMenuItem,
	withDropdownMenu,
	SearchDrawer,
} from "@/components";

import { LOGIN_URL } from "@/lib/constants/auth.constants";
import { useRouter } from "next-nprogress-bar";
import { pathNames } from "@/lib/constants/pathNames.constants";
import { RiShareBoxFill } from "@remixicon/react";

type TNavItem = {
	id: string;
	dropdownItems: TDropdownMenuItem[];
};

type RightActionProps = {
	readonly onToggleOpen: (value: boolean) => void;
	readonly isOpen: boolean;
};

export default function RightActions({
	onToggleOpen,
	isOpen,
}: RightActionProps) {
	const TIconNames = {
		user: "user-m",
		menu: "menu-m",
	} as const;

	const router = useRouter();
	const pathName = usePathname();
	const NavLink = withDropdownMenu(HmcNavLink);
	const {
		isAuthenticated,
		cognitoFullLogout: logout,
		cognitoUserInfo: userInfo,
	} = useAuth();

	const menuNavItems: TNavItem[] = [
		{
			id: "user",
			dropdownItems: [
				{
					id: "ssoLogin",
					label: isAuthenticated
						? `${userInfo?.givenName ?? ""} ${userInfo?.familyName ?? ""}`.trim()
						: t("auth.login"),
					path: LOGIN_URL,
					icon: <HmcIcon name="user-m" />,
				},
				...(isAuthenticated
					? [
							{
								id: "logout",
								label: t("auth.logout"),
								icon: <HmcIcon name="log-out-m" />,
							},
						]
					: []),
			],
		},
		{
			id: "menu",
			dropdownItems: [
				{
					id: "search",
					label: t("search.button-search-title"),
					icon: <HmcIcon name="search-m" />,
				},
				{
					id: "home",
					label: t("header.button-analog"),
					icon: <HmcIcon name="home-m" />,
				},
			],
		},
	];
	return (
		<>
			<div className="hidden gap-3 pr-2 md:flex">
				<div className="hidden items-center gap-1 bg-layoutContainer text-systemInfo md:flex">
					<HmcButton
						kind="ghost"
						size="md"
						hidden={pathName.includes(pathNames.search) || isOpen}
						type="button"
						title={t("search.button-search-title")}
						aria-label={t("search.button-search-title")}
						data-testid="header-nav-button-search"
						onClick={() => {
							onToggleOpen(true);
						}}
					>
						<HmcIcon name="search-m" color="" />

						<span className="hidden">{t("search.button-search-title")}</span>
					</HmcButton>
					<div className="hidden bg-layoutContainer sm:block">
						<HeaderUser />
					</div>
				</div>
				<HmcButton
					kind="secondary"
					size="md"
					type="button"
					href="https://analog.com/"
					target="_blank"
					data-testid="header-nav-button-analog"
				>
					<span className="text-[16px] leading-5">
						{t("header.button-analog")}
					</span>
					<RiShareBoxFill
						size="20"
						// @ts-expect-error <RiShareBoxFill> does not have a slot prop, but it's needed for <HmcButton>
						slot="icon-right"
					/>
				</HmcButton>
			</div>
			<div className="row mr-2 flex text-systemInfo md:flex md:hidden">
				{menuNavItems.map((menuItem) => (
					<NavLink
						key={menuItem.id}
						menuItems={menuItem.dropdownItems}
						size="md"
						role="listitem"
						orientation="right"
						data-testid="dropdown-menu-user"
						onLinkClick={(e: any) => {
							e.preventDefault();
						}}
						onSelection={(item: TDropdownMenuItem) => {
							if (item.id === "search") {
								onToggleOpen(true);
							} else if (item.id === "logout") {
								logout();
							} else {
								router.push(item.path!);
							}
						}}
					>
						<HmcButton
							kind="ghost"
							size="xs"
							isIconOnly={true}
							type="button"
							title={t("search.button-search-title")}
							aria-label={t("search.button-search-title")}
							data-testid="header-nav-button-search"
						>
							<HmcIcon
								name={TIconNames[menuItem.id as keyof typeof TIconNames]}
								color="000"
							/>
						</HmcButton>
					</NavLink>
				))}
			</div>
		</>
	);
}
